import { isFeatureEnabled } from '@luna/gatekeeper';

import { externalProductBalanceRefreshRegistry } from './externalProductBalanceService';
import {
    fetchGAPIAccount,
    selectGAPIAccountBalance,
    selectGAPIAccountProductType,
} from '../gAPIAccountEntitySlice';

const DEFAULT_MAX_ACCOUNT_BALANCE_RETRIES = 3;
const ACCOUNT_BALANCE_REFRESH_STALE_TIME = 10000;
const BACKOFF_DELAY = {
    FIRST: 1,
    SECOND: 3,
    THIRD: 5,
    FOURTH: 7,
    FIFTH: 9,
};
const BACKOFF_DELAY_SECONDS = [
    BACKOFF_DELAY.FIRST,
    BACKOFF_DELAY.SECOND,
    BACKOFF_DELAY.THIRD,
    BACKOFF_DELAY.FOURTH,
    BACKOFF_DELAY.FIFTH,
];
const MULTIPLIER = 1000;

export type RefreshingBalanceMetaDataCache = {
    [accountId: string]: { isRefreshing: boolean; lastUpdated?: number };
};

const refreshingBalanceMetaDataCache: RefreshingBalanceMetaDataCache = {};

export const shouldRefreshBalance = (
    accountId: string,
    cache: RefreshingBalanceMetaDataCache,
): boolean => {
    const { isRefreshing = false, lastUpdated = 0 } = cache[accountId] ?? {};
    if (isRefreshing) {
        return false;
    }

    if (lastUpdated === 0) {
        return true;
    }

    const timeSinceLastUpdate = Date.now() - lastUpdated;

    return timeSinceLastUpdate >= ACCOUNT_BALANCE_REFRESH_STALE_TIME;
};

export const setStartRefreshingBalanceMetaData = (
    accountId: string,
    cache: RefreshingBalanceMetaDataCache,
): void => {
    cache[accountId] = {
        ...cache[accountId],
        isRefreshing: true,
    };
};

export const setEndRefreshingBalanceMetaData = (
    accountId: string,
    cache: RefreshingBalanceMetaDataCache,
): void => {
    cache[accountId] = { isRefreshing: false, lastUpdated: Date.now() };
};

const getDelayTime = (count: number): number => BACKOFF_DELAY_SECONDS[count] * MULTIPLIER;

const delay = (retryCount: number): Promise<void> =>
    new Promise((resolve) => {
        setTimeout(resolve, getDelayTime(retryCount));
    });

export type Options = {
    retryCount?: number;
    maxRetries?: number;
    refetchProducts?(): Promise<void>;
    onSuccess?(count: number): void;
    onFail?(count: number): void;
};

export const refreshBalance = async (
    fetch: () => Promise<void>,
    hasNewValue: () => boolean,
    {
        onSuccess = () => {},
        onFail = () => {},
        retryCount = 0,
        maxRetries = DEFAULT_MAX_ACCOUNT_BALANCE_RETRIES,
    }: Options = {},
): Promise<void> => {
    await fetch();

    if (hasNewValue()) {
        onSuccess(retryCount);
        return;
    }

    const count = retryCount + 1;

    if (count >= maxRetries) {
        onFail(count);
        return;
    }

    await delay(count);

    await refreshBalance(fetch, hasNewValue, { onSuccess, onFail, maxRetries, retryCount: count });
};

export const refreshAccountBalance = async (
    accountId: string,
    options?: Options,
): Promise<void> => {
    if (!shouldRefreshBalance(accountId, refreshingBalanceMetaDataCache)) {
        return;
    }
    const selectAccountBalance = selectGAPIAccountBalance;
    const initialBalanceValue = selectAccountBalance(accountId)?.value;
    const productType = selectGAPIAccountProductType(accountId);

    setStartRefreshingBalanceMetaData(accountId, refreshingBalanceMetaDataCache);

    await refreshBalance(
        async () => {
            fetchGAPIAccount({
                id: accountId,
            }).catch(() => undefined);
        },
        () => selectAccountBalance(accountId)?.value !== initialBalanceValue,
        {
            ...options,

            maxRetries: isFeatureEnabled((f) => f.balanceRefreshService)
                ? DEFAULT_MAX_ACCOUNT_BALANCE_RETRIES
                : 0,
        },
    );

    Object.values(externalProductBalanceRefreshRegistry.getAll()).forEach(
        (externalProductFetcher) => {
            externalProductFetcher(accountId, productType);
        },
    );

    setEndRefreshingBalanceMetaData(accountId, refreshingBalanceMetaDataCache);
};
